import { toast } from 'react-toastify';

import {
  DraftOrderCreateMutation,
  DraftOrderLineCreateMutation,
  ProductVariantAvailabilityEnum,
  useDraftOrderCreateMutation,
  useDraftOrderLineCreateMutation,
} from '@/graphql';
import { ordersStore } from '@/zustand/orders';
import { useGuestStore } from '@/zustand/guestStore';

interface UseAddToCartProps {
  isLoggedIn: boolean | null | undefined;
  availability?: ProductVariantAvailabilityEnum | null | undefined;
  userData: { id: string };
  channelData: any;
  handleCartOpen: () => void;
  draftOrdersData: any;
  refetchDraftOrders: () => void;
}

export const useAddToCart = ({
  isLoggedIn,
  availability,
  userData,
  channelData,
  handleCartOpen,
  draftOrdersData,
  refetchDraftOrders,
}: UseAddToCartProps) => {
  const orderId = useGuestStore(state => state.guestOrderId);

  const setOrders = ordersStore(e => e.setOrders);

  const handleSuccessOrder = (data: DraftOrderCreateMutation) => {
    if (data?.draftOrderCreate?.errors?.length === 0) {
      setOrders(data?.draftOrderCreate?.order);
      refetchDraftOrders();
    }
  };

  const [addProductToCart] = useDraftOrderCreateMutation({
    context: {
      requiresAuth: true,
    },
    onCompleted: handleSuccessOrder,
  });

  const handleSuccessOrderLine = (data: DraftOrderLineCreateMutation) => {
    if (data?.orderLinesCreate?.errors?.length === 0) {
      setOrders(data?.orderLinesCreate?.order);
      refetchDraftOrders();
    }
  };

  const [addProductOrderLineToCart] = useDraftOrderLineCreateMutation({
    context: {
      requiresAuth: true,
    },
    onCompleted: handleSuccessOrderLine,
  });

  const addToCart = async (id: any) => {
    if (availability === 'OUT_OF_STOCK') {
      toast.error(`Товара нет в наличии.`);
      return;
    }
    if (availability === 'COMING_SOON') {
      toast.info(`Товар скоро появится.`);
      return;
    }

    try {
      if (isLoggedIn) {
        if (draftOrdersData?.userDraftOrders?.length === 0) {
          await addProductToCart({
            variables: {
              input: {
                channelId: channelData?.channel?.id,
                user: userData?.id,
                lines: [
                  {
                    variantId: id,
                    quantity: 1,
                  },
                ],
              },
            },
          });
        }
        if (draftOrdersData?.userDraftOrders?.[0]?.isFastBuy === false) {
          await addProductOrderLineToCart({
            variables: {
              id: draftOrdersData?.userDraftOrders?.[0]?.id,
              input: [
                {
                  variantId: id,
                  quantity: 1,
                },
              ],
            },
          });
        }
      } else {
        if (!orderId) {
          const orderGuestData = await addProductToCart({
            variables: {
              input: {
                channelId: channelData?.channel?.id,
                lines: [
                  {
                    variantId: id,
                    quantity: 1,
                  },
                ],
              },
            },
          });
          const guestOrderId =
            orderGuestData?.data?.draftOrderCreate?.order?.id;
          typeof guestOrderId === 'string' &&
            useGuestStore.getState().setGuestOrderId(guestOrderId);
        } else {
          await addProductOrderLineToCart({
            variables: {
              id: orderId,
              input: [
                {
                  variantId: id,
                  quantity: 1,
                },
              ],
            },
          });
        }
      }
      handleCartOpen();
    } catch (error) {
      console.error('Error on adding item to basket: ', error);
    }
  };

  return { addToCart };
};
